<template>
  <b-card v-if="history && history.length > 0" class="mt-2">
    <b-card-title>{{ $t('auditLog.title') }}</b-card-title>

    <b-table
      :items="sortedHistory"
      :fields="tableColumns"
      striped
      responsive
      small
      :empty-text="$t('auditLog.noEntries')"
      show-empty
    >
      <template #cell(timestamp)="data">
        {{ formatDate(data.item.timestamp) }}
      </template>

      <template #cell(userName)="data">
        {{ data.item.userName || '-' }}
      </template>

      <template #cell(action)="data">
        <b-badge :variant="getActionVariant(data.item.action)">
          {{ translateAction(data.item.action) }}
        </b-badge>
      </template>

      <template #cell(changedFields)="data">
        <div v-if="hasChangedFields(data.item)">
          <div
            v-for="(change, fieldName) in data.item.changedFields"
            :key="fieldName"
            class="mb-50"
          >
            <strong>{{ translateField(fieldName) }}:</strong>
            <span class="text-muted ml-50">
              {{ formatValueWithTranslation(change.from, fieldName) }} → {{ formatValueWithTranslation(change.to, fieldName) }}
            </span>
          </div>
        </div>
        <div v-else-if="data.item.detail">
          <span>{{ data.item.detail }}</span>
        </div>
        <span v-else class="text-muted">-</span>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import { computed } from '@vue/composition-api'
import {
  BCard, BCardTitle, BTable, BBadge,
} from 'bootstrap-vue'
import { formatFieldValue } from '@/helpers/auditLog'

export default {
  name: 'AuditLogTable',
  components: {
    BCard,
    BCardTitle,
    BTable,
    BBadge,
  },
  props: {
    history: {
      type: Array,
      default: () => [],
    },
    fieldTranslations: {
      type: Object,
      default: () => ({}),
    },
    stageTranslationPrefix: {
      type: String,
      default: null,
    },
  },
  setup(props, { root }) {
    const tableColumns = computed(() => [
      { key: 'timestamp', label: root.$t('auditLog.date'), sortable: true },
      { key: 'userName', label: root.$t('auditLog.user'), sortable: true },
      { key: 'action', label: root.$t('auditLog.action'), sortable: true },
      { key: 'changedFields', label: root.$t('auditLog.changedFields') },
    ])

    const sortedHistory = computed(() => {
      if (!props.history) return []
      return [...props.history].sort((a, b) => {
        const dateA = new Date(a.timestamp || a.timestamp?.$date)
        const dateB = new Date(b.timestamp || b.timestamp?.$date)
        return dateB - dateA
      })
    })

    const formatDate = timestamp => {
      if (!timestamp) return '-'
      const date = timestamp.$date ? new Date(timestamp.$date) : new Date(timestamp)
      return date.toLocaleString('es-CL')
    }

    const getActionVariant = action => {
      const variants = {
        crear: 'success',
        actualizar: 'info',
        eliminar: 'danger',
        cambio_status: 'warning',
        status_changed: 'warning',
        updated: 'info',
        validar: 'success',
        rechazar: 'danger',
        desactivar: 'danger',
        envio_recordatorio: 'primary',
        envio_email: 'info',
        subir_nueva_version: 'primary',
        editar_metadatos: 'warning',
      }
      return variants[action] || 'secondary'
    }

    const translateAction = action => {
      const translationKey = `auditLog.actions.${action}`
      const translated = root.$t(translationKey)
      // Si la traducción es igual a la key, significa que no existe
      if (translated === translationKey) return action
      // Capitalizar primera letra
      return translated.charAt(0).toUpperCase() + translated.slice(1)
    }

    const translateField = fieldName => {
      // Primero intentar traducciones custom pasadas como prop
      if (props.fieldTranslations[fieldName]) {
        return props.fieldTranslations[fieldName]
      }
      // Luego intentar traducción general de fields
      const translationKey = `fields.${fieldName}`
      const translated = root.$t(translationKey)
      // Si no existe la traducción, usar el nombre del campo
      return translated !== translationKey ? translated : fieldName
    }

    const formatValue = value => formatFieldValue(value)

    const formatValueWithTranslation = (value, fieldName) => {
      // Si es el campo stage y tenemos un prefijo de traducción, traducir el valor
      if (fieldName === 'stage' && props.stageTranslationPrefix && value) {
        const translationKey = `${props.stageTranslationPrefix}.${value}`
        const translated = root.$t(translationKey)
        // Si la traducción existe, usarla; sino, usar formatFieldValue
        if (translated !== translationKey) return translated
      }

      // Para otros campos o si no hay traducción, usar formateo normal
      return formatFieldValue(value)
    }

    const hasChangedFields = item => item.changedFields && Object.keys(item.changedFields).length > 0

    return {
      tableColumns,
      sortedHistory,
      formatDate,
      getActionVariant,
      translateAction,
      translateField,
      formatValue,
      formatValueWithTranslation,
      hasChangedFields,
    }
  },
}
</script>
